import { inject } from "vue";
import { Contacts } from "acquaint-api/models/Contacts";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { IdNamePair } from "@/types/generic/IdNamePair";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";

export function useContactHelper() {
  const acquaintApiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const apiNextClient = inject<AcquaintApiNextClient>("AcquaintApiNextClient")!;

  return {
    fetchContactById: async (contactId: number) => {
      try {
        if (contactId > 0) {
          const contact = await acquaintApiClient.httpGet<Contacts>(
            `database/contacts/${contactId}`,
          );
          return contact;
        }
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    updateContact: async (contact: Contacts) => {
      try {
        if (!contact || !contact.id) return;

        await acquaintApiClient.httpPut<Contacts>(
          `database/contacts/${contact.id}`,
          contact,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    getSystemContactTypeByTypeId: async (contactTypeId: number) => {
      try {
        const type = await acquaintApiClient.httpGet<number>(
          `app/contacts/${contactTypeId}/systemcontacttype`,
        );
        return type;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    // only linked contacts
    fetchLinkedContacts: async (contactId: number) => {
      try {
        const linkedContacts = await apiNextClient.httpGet<IdNamePair[]>(
          `/contacts/${contactId}/links`,
        );
        return linkedContacts;
      } catch (err) {
        displayErrorMessage("The linked contacts could not be fetched");
        return [];
      }
    },

    // contact with linked contacts
    fetchContactWithLinks: async (id: number) => {
      try {
        return await acquaintApiClient.httpGet<IDDescriptionPair[]>(
          `app/contacts/${id}/links`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    // Lock/unlock
    lockContact: async (contactId: number, lock: boolean) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `database/contacts/${contactId}/lock`,
          {
            lock,
            extraInfo: "",
          },
        );
        return true;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },
  };
}
